import * as React from 'react';
import {Switch, Route} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {createSelectedAddOnObj} from 'shells/billing/add-ons/helpers';
// @ts-expect-error
import {ADD_ONS_HASHES, ENGAGEMENT_FOUNDATION_MONTHLY, MARKETING_HASH} from 'shells/billing/consts';
import type {Cycle, SelectedAddOn, CachedPlan} from 'shells/billing/types';
import {Dialog} from 'shells/dialog/dialog';
import {PageHeader} from 'shells/page-header/page-header';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer} from '../../../layout';
import {ChatWithUs} from '../../../onboarding/trial-banner/chat-with-us';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';
import {readLocalStorage} from '../../../../util/localStorage';

import {
    useCachePlanSelection,
    useGetAccountBilling,
    useGetAvailablePlans,
    useGetBillingInfo,
    useGetInvoicePreview,
    useUpdatePlanSelection,
} from '../graphql/hooks';
import {
    getCycleFromPlanCode,
    DEFAULT_CYCLE,
    shouldDisablePlanViewButton,
    shouldDisableAddOnViewButton,
    shouldDisableEngagementViewButton,
} from '../helpers';

import {EngagementSuitePicker} from './engagement/engagement-suite-picker';
import {
    getCrmPlanDetails,
    replaceCustomPlansWithActivePlans,
    filterIncludedAddOns,
    getEngagementPlanDetails,
    getMarketingPlanDetails,
    isFakePlan,
    removeRedundantAddOns,
    getScrollContainerId,
    getRouteFromPath,
    navigateToRoute,
    buildSelectedAddOnsCacheInput,
    getCrmPlanPriceDifference,
} from './helpers';
import {MarketingSuitePicker} from './marketing/marketing-suite-picker';
import {SalesSuitePicker} from './sales/sales-suite-picker';

import {Footer, ManageAddons, Review, SuccessState} from '.';

import styles from './manage.module.css';

export function Manage(props: ContextRouter) {
    const {location} = props;

    const [selectedAddOns, setSelectedAddOns] = React.useState<SelectedAddOn[]>([]);
    const [selectedCrmPlanCode, setSelectedCrmPlanCode] = React.useState<string | null | undefined>(
        null
    );
    const [selectedEngagementPlanCode, setSelectedEngagementPlanCode] = React.useState<
        string | null | undefined
    >(null);
    const [selectedMarketingSuitePlanCode, setSelectedMarketingSuitePlanCode] = React.useState<
        string | null | undefined
    >(null);
    const [marketingSuiteCycle, setMarketingSuiteCycle] = React.useState<Cycle>(DEFAULT_CYCLE);
    const [numUsers, setNumUsers] = React.useState<string | null | undefined>(null);
    const [numEngagementUsers, setNumEngagementUsers] = React.useState<string | null | undefined>(
        '1'
    );
    const [cycle, setCycle] = React.useState<Cycle>(DEFAULT_CYCLE);
    const [couponCode, setCouponCode] = React.useState<string | null | undefined>(null);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = React.useState<boolean>(false);
    const [isViewingBillingForm, setIsViewingBillingForm] = React.useState<boolean>(false);
    const [isPlansInitialized, setIsPlansInitialized] = React.useState<boolean>(false); // Used to prevent caching plans when they aren't initialized
    const [hasUnsavedBillingChanges, setHasUnsavedBillingChanges] = React.useState<boolean>(false);

    const {
        sessionInstance,
        refetch: refetchSessionInstance,
        isLoading: isLoadingSessionInstance,
    } = useGetInstanceInfo();
    const {user} = useGetSessionUser();
    const isTrial = Boolean(sessionInstance && sessionInstance.isTrial);
    const {accountBilling, refetch: refetchAccountBilling} = useGetAccountBilling();
    const {
        billingInfo,
        isLoading: isLoadingBillingInfo,
        refetch: refetchBillingInfo,
        errorMessage: billingInfoErrorMessage,
    } = useGetBillingInfo();
    const {
        addOns: addOnsWithRedundantPlans,
        crm,
        engagement,
        marketingSuite,
        refetch: refetchPlans,
        isLoading: isLoadingPlans,
        errorMessage: availablePlansErrorMessage,
    } = useGetAvailablePlans();
    const {
        updatePlanSelection,
        isUpdating,
        errorMessage: errorUpdatingPlan,
    } = useUpdatePlanSelection();
    const {cachePlanSelection} = useCachePlanSelection();

    const addOns = removeRedundantAddOns(
        addOnsWithRedundantPlans,
        selectedMarketingSuitePlanCode,
        selectedEngagementPlanCode
    );

    const route = getRouteFromPath(location.pathname);

    const crmPlans = crm ? crm.plans : [];
    const engagementPlans = engagement ? engagement.plans : [];
    const marketingPlans = marketingSuite ? marketingSuite.plans : [];

    // Fake plans should not accounted for in the invoice preview
    const isMarketingSuitePlanFake = isFakePlan(selectedMarketingSuitePlanCode, marketingPlans);
    const isEngagementPlanFake = isFakePlan(selectedEngagementPlanCode, engagementPlans);

    const scrollContainerId = getScrollContainerId(route);

    const {
        invoicePreview,
        isLoading,
        errorMessage: invoicePreviewError,
    } = useGetInvoicePreview({
        // @ts-expect-error
        crmPlanCode: selectedCrmPlanCode,
        // @ts-expect-error
        numberOfUsers: parseInt(numUsers),
        engagementPlanCode: isEngagementPlanFake ? null : selectedEngagementPlanCode,
        numberOfEngagementUsers: isEngagementPlanFake ? 0 : parseInt(numEngagementUsers || '0'),
        marketingSuitePlanCode: isMarketingSuitePlanFake ? null : selectedMarketingSuitePlanCode,
        addOns: replaceCustomPlansWithActivePlans(selectedAddOns, addOns), // we do not factor custom plans into the invoice preview
        couponCode,
    });

    const getPageTitle = () => {
        if (!isLoadingSessionInstance && sessionInstance) {
            return isTrial ? 'Upgrade to a paid plan' : 'Manage your plan';
        }

        return '';
    };

    const backButtonRef = React.useRef(null);
    const selectedAddOnsInitializationRef = React.useRef(false);

    React.useEffect(() => {
        if (isTrial && isPlansInitialized) {
            const crmInput: CachedPlan | null | undefined = selectedCrmPlanCode
                ? {
                      planCode: selectedCrmPlanCode,
                      licenseCount: numUsers ? Number.parseInt(numUsers) : undefined,
                  }
                : null;
            const marketingSuiteInput: CachedPlan | null | undefined =
                selectedMarketingSuitePlanCode
                    ? {
                          planCode: selectedMarketingSuitePlanCode,
                          licenseCount: null,
                      }
                    : null;
            const engagementPlanInput: CachedPlan | null | undefined = selectedEngagementPlanCode
                ? {
                      planCode: selectedEngagementPlanCode,
                      licenseCount: numEngagementUsers ? Number.parseInt(numEngagementUsers) : null,
                  }
                : null;

            const addOnsCacheInput = buildSelectedAddOnsCacheInput(selectedAddOns);

            cachePlanSelection({
                crm: crmInput,
                marketingSuite: marketingSuiteInput,
                inbox: engagementPlanInput,
                marketing: addOnsCacheInput.marketing,
                nutshellIq: addOnsCacheInput.nutshellIq,
                smsCampaigns: addOnsCacheInput.smsCampaigns,
                quotes: addOnsCacheInput.quotes,
                nutshellAdvisor: addOnsCacheInput.nutshellAdvisor,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isTrial,
        isPlansInitialized,
        selectedCrmPlanCode,
        numUsers,
        selectedMarketingSuitePlanCode,
        selectedEngagementPlanCode,
        numEngagementUsers,
        selectedAddOns,
    ]);

    React.useEffect(() => {
        if (crm) {
            if (!selectedCrmPlanCode) {
                // When we set our CRM code, we can assume our plans are
                // initialized and we can begin caching next render
                setIsPlansInitialized(true);
                if (crm.cachedPlan) {
                    const cachedPlan = crm.cachedPlan;
                    setSelectedCrmPlanCode(cachedPlan.code);
                    const planCodeCycle = getCycleFromPlanCode(cachedPlan.code);
                    setCycle(planCodeCycle);
                    // We set numUsers below in a different useEffect

                    return;
                }

                setSelectedCrmPlanCode(crm.defaultCode);

                const activePlan = crm.plans.find((plan) => plan.isActive);
                if (activePlan) {
                    const planCodeCycle = getCycleFromPlanCode(activePlan.code);

                    setSelectedCrmPlanCode(activePlan.code);
                    setNumUsers(`${activePlan.quantity}`);
                    setCycle(planCodeCycle);
                } else if (NutClientConfig.isInternal) {
                    // For webinar demoing purposes, we want to fill the
                    // numUsers so the demoer can continue past the plan
                    // selector screen without manually inputting the number
                    setNumUsers(`${crm.enabledUsers}`);
                }
            }
        }
    }, [crm, selectedCrmPlanCode]);

    React.useEffect(() => {
        const setDefaultNumEngagementUsers = (numBilledLicenses: number) => {
            setNumEngagementUsers(`${Math.max(1, numBilledLicenses)}`);
        };

        if (engagement) {
            if (!selectedEngagementPlanCode) {
                if (engagement.cachedPlan) {
                    const cachedPlan = engagement.cachedPlan;
                    setSelectedEngagementPlanCode(cachedPlan.code);

                    if (cachedPlan.licenseCount) {
                        setNumEngagementUsers(`${cachedPlan.licenseCount}`);
                    } else {
                        setDefaultNumEngagementUsers(engagement.numBilledLicenses);
                    }

                    return;
                }

                setSelectedEngagementPlanCode(ENGAGEMENT_FOUNDATION_MONTHLY);

                const activePlan = engagement.plans.find((plan) => plan.isActive);

                setDefaultNumEngagementUsers(engagement.numBilledLicenses);
                if (activePlan) {
                    setSelectedEngagementPlanCode(activePlan.code);
                }
            }
        }
    }, [engagement, selectedEngagementPlanCode]);

    React.useEffect(() => {
        if (marketingSuite) {
            if (!selectedMarketingSuitePlanCode) {
                if (marketingSuite.cachedPlan) {
                    setSelectedMarketingSuitePlanCode(marketingSuite.cachedPlan.code);

                    return;
                }

                setSelectedMarketingSuitePlanCode(marketingSuite.defaultCode);

                const activePlan = marketingSuite.plans.find((plan) => plan.isActive);
                if (activePlan) {
                    const planCodeCycle = getCycleFromPlanCode(activePlan.code);
                    setSelectedMarketingSuitePlanCode(activePlan.code);
                    setMarketingSuiteCycle(planCodeCycle);
                }
            }
        }
    }, [marketingSuite, selectedMarketingSuitePlanCode]);

    React.useEffect(() => {
        if (typeof numUsers !== 'string' && isTrial && crm) {
            if (crm.cachedPlan && crm.cachedPlan.licenseCount && crm.cachedPlan.licenseCount > 0) {
                setNumUsers(`${crm.cachedPlan.licenseCount}`);
            } else {
                setNumUsers(`${crm.enabledUsers}`);
            }
        }
    }, [isTrial, numUsers, crm]);

    const isModified = invoicePreview && invoicePreview.isModified;
    const isPlanErrored =
        crm &&
        shouldDisablePlanViewButton(crm.enabledUsers, numUsers, isTrial, crm.numMarketingLicenses);
    const isPlanViewButtonDisabled = route === 'sales' && (!selectedCrmPlanCode || isPlanErrored);
    const isEngagementViewButtonDisabled =
        route === 'engagement' &&
        selectedEngagementPlanCode !== ENGAGEMENT_FOUNDATION_MONTHLY &&
        (!selectedEngagementPlanCode ||
            shouldDisableEngagementViewButton(
                engagement && engagement.numEnabledLicenses,
                numEngagementUsers
            ));
    const isMarketingViewButtonDisabled = route === 'marketing' && !selectedMarketingSuitePlanCode;
    const isAddOnViewButtonDisabled =
        route === 'add-ons' && shouldDisableAddOnViewButton(addOns, selectedAddOns);
    const isPurchaseButtonDisabled =
        route === 'review' &&
        (!billingInfo || !billingInfo.isValid || !isModified || isViewingBillingForm);

    const isButtonDisabled =
        isPlanViewButtonDisabled ||
        isEngagementViewButtonDisabled ||
        isMarketingViewButtonDisabled ||
        isAddOnViewButtonDisabled ||
        isPurchaseButtonDisabled;

    const planDetails = getCrmPlanDetails(selectedCrmPlanCode, crmPlans, numUsers);
    const engagementDetails = getEngagementPlanDetails(
        selectedEngagementPlanCode,
        engagementPlans,
        selectedEngagementPlanCode === ENGAGEMENT_FOUNDATION_MONTHLY ? numUsers : numEngagementUsers
    );
    const marketingDetails = getMarketingPlanDetails(
        selectedMarketingSuitePlanCode,
        marketingPlans
    );

    const handleNext = () => {
        if (route === 'sales') {
            navigateToRoute('marketing');
        } else if (route === 'marketing') {
            navigateToRoute('engagement');
        } else if (route === 'engagement') {
            navigateToRoute('add-ons');
        } else if (route === 'add-ons') {
            navigateToRoute('review');
        } else if (route === 'review' && numUsers && selectedCrmPlanCode) {
            // If a user selects custom plans, we want to leave any current plans in place
            // since the user will need to define their plan with support
            updatePlanSelection({
                crmPlanCode: selectedCrmPlanCode,
                numberOfUsers: Number.parseInt(numUsers),
                engagementPlanCode:
                    selectedEngagementPlanCode === ENGAGEMENT_FOUNDATION_MONTHLY
                        ? null
                        : selectedEngagementPlanCode,
                marketingSuitePlanCode: isMarketingSuitePlanFake
                    ? null
                    : selectedMarketingSuitePlanCode,
                numberOfEngagementUsers: isEngagementPlanFake
                    ? 0
                    : Number.parseInt(numEngagementUsers || '0'),
                addOns: filterIncludedAddOns(
                    replaceCustomPlansWithActivePlans(selectedAddOns, addOns),
                    invoicePreview
                ),
                couponCode,
            }).then(() => {
                // get new active plan info
                refetchPlans();

                // get updated invoices
                refetchAccountBilling();

                // get updated isTrial value
                refetchSessionInstance();

                // route to churn page when crm spend decreases by more than $100
                const crmPlanPriceDifference = getCrmPlanPriceDifference(
                    accountBilling,
                    invoicePreview
                );

                if (crmPlanPriceDifference < -100) {
                    const userEmail =
                        user && user.emailAddresses && user.emailAddresses.length > 0
                            ? // @ts-expect-error
                              user.emailAddresses[0].address
                            : null;
                    analyticsClient.track(analyticsClient.EVENTS.TRACK_CHURN_SURVEY_TRIGGERED, {
                        priceDifference: crmPlanPriceDifference,
                        newMrr:
                            invoicePreview && invoicePreview.plan && invoicePreview.plan.totalMonth,
                        userEmail,
                        companyName: sessionInstance && sessionInstance.companyName,
                    });

                    navigateToRoute('churn', {priceDifference: crmPlanPriceDifference.toString()});
                } else {
                    navigateToRoute('success');
                }
            });
        }
    };

    React.useEffect(() => {
        if (addOns && !selectedAddOnsInitializationRef.current && isPlansInitialized) {
            const initialSelectedAddOns: SelectedAddOn[] = [];

            const addOnKeys = Object.keys(addOns);
            addOnKeys.forEach((addOnKey) => {
                // @ts-expect-error
                if (addOns[addOnKey].plans.length === 0) {
                    return;
                }

                // @ts-expect-error
                const addOn = addOns[addOnKey];
                const product = addOn.plans[0].product;
                // @ts-expect-error
                const activePlan = addOn.plans.find((plan) => plan.isActive);

                if (activePlan) {
                    initialSelectedAddOns.push({
                        product: product,
                        planCode: activePlan.code,
                        quantity: addOn.isUserQuantityBased ? activePlan.quantity : null,
                    });
                }

                if (addOn.cachedPlan) {
                    initialSelectedAddOns.push({
                        product: product,
                        planCode: addOn.cachedPlan.code,
                        quantity: addOn.isUserQuantityBased ? addOn.cachedPlan.licenseCount : null,
                    });
                }
            });

            // If the client is arriving to the page via add-on hash, we want to
            // add that add-on to the selectedAddOns list so it is automatically
            // selected upon page load.
            const hash = window.location.hash;
            const selectedAddOn = createSelectedAddOnObj(hash, addOns);
            if (
                selectedAddOn &&
                !initialSelectedAddOns.some((addOn) => addOn.product === selectedAddOn.product)
            ) {
                initialSelectedAddOns.push(selectedAddOn);
            }

            setSelectedAddOns((prevSelectedAddOns) => [
                ...prevSelectedAddOns,
                ...initialSelectedAddOns,
            ]);
            selectedAddOnsInitializationRef.current = true;
        }
    }, [addOns, isPlansInitialized]);

    React.useEffect(() => {
        const hash = window.location.hash;
        if (ADD_ONS_HASHES.includes(hash)) {
            navigateToRoute('add-ons');
        }

        if (hash === MARKETING_HASH) {
            navigateToRoute('marketing');
        }

        // Ensures we hit the success page after the post-purchase refresh.
        if (readLocalStorage('billingSuccess')) {
            navigateToRoute('success');
        }
    }, []);

    React.useEffect(() => {
        if (!isLoadingBillingInfo && !billingInfo) {
            setIsViewingBillingForm(true);
        }
    }, [billingInfo, isLoadingBillingInfo]);

    const getButtonTooltip = () => {
        if (!isPurchaseButtonDisabled) {
            return '';
        }

        if (!isModified) {
            return 'Please make changes to your plan to complete a purchase';
        } else if (isViewingBillingForm) {
            return 'Please finish updating your billing information';
        } else if (!billingInfo || !billingInfo.isValid) {
            return 'Please enter valid billing information';
        }

        return '';
    };

    return (
        <PageContainer
            title='Billing'
            pageTitleBar={
                <PageHeader
                    backButton={
                        route === 'success'
                            ? undefined
                            : {
                                  text: 'Back to Billing home',
                                  onClick: () => {
                                      if (isModified) {
                                          setIsConfirmationDialogOpen(true);
                                      } else {
                                          navigateToRoute();
                                      }
                                  },
                                  getRef: (ref) => {
                                      // @ts-expect-error
                                      backButtonRef.current = ref;
                                  },
                              }
                    }
                    title={getPageTitle()}
                    subtitle={undefined}
                    actionButtons={<ChatWithUs />}
                />
            }
            darkBackground={true}
            noPadding={true}
        >
            {isConfirmationDialogOpen && (
                <Dialog
                    anchor={backButtonRef.current}
                    question='Uh oh! You’re about to lose your changes. Are you sure you’d like to cancel your changes?'
                    primaryText='Yes, cancel changes'
                    secondaryText='No, keep editing'
                    onPrimary={() => {
                        appHistory.push('/setup/billing');
                    }}
                    onSecondary={() => setIsConfirmationDialogOpen(false)}
                />
            )}
            <div className='pos-relative full-height flex flex-dir-col background-white'>
                <Switch>
                    <Route
                        path='/setup/billing/sales'
                        render={() => (
                            <div id={scrollContainerId} className={styles['scroll-container']}>
                                <SalesSuitePicker
                                    plans={crmPlans}
                                    errorMessage={availablePlansErrorMessage}
                                    isLoadingPlans={isLoadingPlans}
                                    selectedPlanCode={selectedCrmPlanCode}
                                    numUsers={numUsers}
                                    numEnabledUsers={crm ? crm.enabledUsers : undefined}
                                    numMarketingLicenses={
                                        crm ? crm.numMarketingLicenses : undefined
                                    }
                                    cycle={cycle}
                                    setSelectedCrmPlanCode={setSelectedCrmPlanCode}
                                    setNumUsers={setNumUsers}
                                    setCycle={setCycle}
                                    isTrial={isTrial}
                                    scrollContainerId={scrollContainerId}
                                />
                            </div>
                        )}
                    />
                    <Route
                        path='/setup/billing/engagement'
                        render={() => (
                            <div id={scrollContainerId} className={styles['scroll-container']}>
                                <EngagementSuitePicker
                                    plans={engagementPlans}
                                    selectedPlanCode={selectedEngagementPlanCode}
                                    setSelectedEngagementPlanCode={setSelectedEngagementPlanCode}
                                    isLoadingPlans={isLoadingPlans}
                                    errorMessage={availablePlansErrorMessage}
                                    numUsers={numEngagementUsers || ''}
                                    setNumUsers={setNumEngagementUsers}
                                    numSalesUsers={numUsers || ''}
                                    numEnabledLicenses={engagement && engagement.numEnabledLicenses}
                                    scrollContainerId={scrollContainerId}
                                    isTrial={isTrial}
                                />
                            </div>
                        )}
                    />
                    <Route
                        path='/setup/billing/marketing'
                        render={() => (
                            <div id={scrollContainerId} className={styles['scroll-container']}>
                                <MarketingSuitePicker
                                    plans={marketingPlans}
                                    cycle={marketingSuiteCycle}
                                    setCycle={setMarketingSuiteCycle}
                                    selectedPlanCode={selectedMarketingSuitePlanCode}
                                    setSelectedMarketingPlanCode={setSelectedMarketingSuitePlanCode}
                                    isLoadingPlans={isLoadingPlans}
                                    errorMessage={availablePlansErrorMessage}
                                    numSalesUsers={numUsers || ''}
                                    addOns={addOns}
                                    selectedAddOns={selectedAddOns}
                                    setSelectedAddOns={setSelectedAddOns}
                                    isTrial={isTrial}
                                    scrollContainerId={scrollContainerId}
                                />
                            </div>
                        )}
                    />
                    <Route
                        path='/setup/billing/add-ons'
                        render={() => (
                            <div id={scrollContainerId} className={styles['scroll-container']}>
                                <ManageAddons
                                    addOns={addOns}
                                    route={route}
                                    selectedAddOns={selectedAddOns}
                                    setSelectedAddOns={setSelectedAddOns}
                                    errorMessage={availablePlansErrorMessage}
                                    isTrial={isTrial}
                                    invoicePreview={invoicePreview}
                                />
                            </div>
                        )}
                    />
                    <Route
                        path='/setup/billing/review'
                        render={() => (
                            <div id={scrollContainerId} className={styles['scroll-container']}>
                                <Review
                                    invoicePreview={invoicePreview}
                                    invoicePreviewError={invoicePreviewError}
                                    isLoading={isLoading}
                                    couponCode={couponCode}
                                    onChangeCouponCode={setCouponCode}
                                    billingInfo={billingInfo}
                                    billingInfoError={billingInfoErrorMessage}
                                    isLoadingBillingInfo={isLoadingBillingInfo}
                                    refetchBillingInfo={refetchBillingInfo}
                                    errorUpdatingPlan={errorUpdatingPlan}
                                    isViewingBillingForm={isViewingBillingForm}
                                    setIsViewingBillingForm={setIsViewingBillingForm}
                                    isTrial={isTrial}
                                    setHasUnsavedBillingChanges={setHasUnsavedBillingChanges}
                                />
                            </div>
                        )}
                    />
                    <Route
                        path='/setup/billing/success'
                        render={() => <SuccessState isTrial={isTrial} />}
                    />
                </Switch>
                {route !== 'success' && (
                    <Footer
                        route={route}
                        onNext={handleNext}
                        dueCost={
                            !invoicePreviewError && invoicePreview
                                ? invoicePreview.billingCycleDisplay
                                : undefined
                        }
                        isLoading={isLoading}
                        isButtonDisabled={isButtonDisabled}
                        buttonTooltip={getButtonTooltip()}
                        isSaving={isUpdating}
                        planDetails={planDetails}
                        engagementDetails={engagementDetails}
                        marketingDetails={marketingDetails}
                        accountCoupons={
                            invoicePreview && invoicePreview.plan
                                ? invoicePreview.plan.accountCoupons
                                : []
                        }
                        hasUnsavedBillingChanges={hasUnsavedBillingChanges}
                    />
                )}
            </div>
        </PageContainer>
    );
}
